<template>
  <v-row>
    <v-col cols="12">
      <v-row>
        <v-col cols="12" sm="12" md="4" lg="2">
          <v-select
            outlined
            flat
            dense
            background-color="white"
            v-model="selectedYear"
            :items="yearOptions"
            label="Year"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="12" md="4" lg="2">
          <v-select
            outlined
            flat
            dense
            background-color="white"
            v-model="selectedMonth"
            :items="monthOptions"
            item-text="label"
            item-value="value"
            label="Month"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="12" md="4" lg="2">
          <v-btn color="primary" @click="search" class="mr-2">
            <v-icon>
              mdi-magnify
            </v-icon>
          </v-btn>
          <v-btn color="success" @click="processDepreciation" class="mr-2">
            <v-icon>
              mdi-cog
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-col>

    <!-- Loop through categories -->
    <v-col cols="12" v-for="(category, index) in items" :key="index">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ category.category }}</span>
          <v-btn color="primary" small @click="openItemsDialog(category)">
            <v-icon left small>mdi-eye</v-icon>
            View Items
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="journalHeaders"
            :items="category.journals"
            dense
            hide-default-footer
            disable-pagination
          >
            <template v-slot:item.no="{ index }">
              {{ index + 1 }}
            </template>
            <template v-slot:item.amount="{ item }">
              {{ formatPrice(item.amount) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Dialog for Items -->
    <v-dialog v-model="itemsDialog" max-width="1200px">
      <v-card>
        <v-card-title class="headline">
          {{ selectedCategory ? selectedCategory.category : "" }} - Assets
          <v-spacer></v-spacer>
          <v-btn icon @click="itemsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="itemHeaders"
            :items="selectedCategory ? selectedCategory.items : []"
            dense
            hide-default-footer
            disable-pagination
            :search="itemFilter"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-row class="mt-2">
                  <v-col cols="12" sm="12" md="8" lg="8">
                    <v-text-field
                      flat
                      dense
                      outlined
                      background-color="white"
                      v-model="itemFilter"
                      placeholder="search"
                      append-icon="mdi-magnify"
                      single-line
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-toolbar>
            </template>
            <template v-slot:item.no="{ index }">
              {{ index + 1 }}
            </template>
            <template v-slot:item.assetCost="{ item }">
              {{ formatPrice(item.assetCost) }}
            </template>
            <template v-slot:item.depreciationAmount="{ item }">
              {{ formatPrice(item.depreciationAmount) }}
            </template>
            <template v-slot:item.remainingBookValue="{ item }">
              {{ formatPrice(item.remainingBookValue) }}
            </template>
            <template v-slot:item.willBeFullyDepreciated="{ item }">
              <v-chip :color="item.willBeFullyDepreciated ? 'success' : 'grey'" small>
                {{ item.willBeFullyDepreciated ? "Yes" : "No" }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="itemsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
export default {
  name: "depreciation-manual",
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    return {
      selectedYear: currentYear,
      selectedMonth: currentMonth > 1 ? currentMonth - 1 : 1,
      yearOptions: [],
      allMonthOptions: [
        { label: "January", value: 1 },
        { label: "February", value: 2 },
        { label: "March", value: 3 },
        { label: "April", value: 4 },
        { label: "May", value: 5 },
        { label: "June", value: 6 },
        { label: "July", value: 7 },
        { label: "August", value: 8 },
        { label: "September", value: 9 },
        { label: "October", value: 10 },
        { label: "November", value: 11 },
        { label: "December", value: 12 },
      ],
      items: [],
      itemsDialog: false,
      selectedCategory: null,
      itemFilter: "",
      journalHeaders: [
        { text: "No", value: "no", width: "80px" },
        { text: "Account Number", value: "accountNumber" },
        { text: "Account Name", value: "accountName" },
        { text: "Type", value: "type" },
        { text: "Amount", value: "amount", align: "right" },
      ],
      itemHeaders: [
        { text: "No", value: "no", width: "80px" },
        { text: "Description", value: "description" },
        { text: "Asset Cost", value: "assetCost", align: "right" },
        { text: "Quantity", value: "quantity" },
        { text: "Depreciation Amount", value: "depreciationAmount", align: "right" },
        { text: "Remaining Book Value", value: "remainingBookValue", align: "right" },
        { text: "Method", value: "depreciationMethod" },
        { text: "Fully Depreciated", value: "willBeFullyDepreciated" },
      ],
    };
  },
  computed: {
    monthOptions() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      // If selected year is current year, limit to current month - 1
      if (this.selectedYear === currentYear) {
        const maxMonth = currentMonth - 1;
        return this.allMonthOptions.filter(month => month.value <= maxMonth);
      }

      // For previous years, show all months
      return this.allMonthOptions;
    },
  },
  methods: {
    async search() {
      this.$store.commit("SET_LOADER", true);
      try {
        const response = await this.$store.dispatch("fixedAsset/depreciationPreview", {
          year: this.selectedYear,
          month: this.selectedMonth,
        });
        if (response.status === 200) {
          this.items = response.data;
          this.$store.dispatch("toast", {
            type: "success",
            message: "Data loaded successfully",
          });
        }
      } catch (error) {
        this.$store.dispatch("toast", {
          type: "error",
          message: "Failed to load data",
        });
      } finally {
        this.$store.commit("SET_LOADER", false);
      }
    },
    async processDepreciation() {
      this.$store.commit("SET_LOADER", true);
      try {
        const response = await this.$store.dispatch("fixedAsset/depreciationManual", {
          year: this.selectedYear,
          month: this.selectedMonth,
        });
        if (response.status === 200) {
          this.$store.dispatch("toast", {
            type: "success",
            message: "Depreciation processed successfully",
          });
          // Refresh the data after processing
          await this.search();
        }
      } catch (error) {
        this.$store.dispatch("toast", {
          type: "error",
          message: "Failed to process depreciation",
        });
      } finally {
        this.$store.commit("SET_LOADER", false);
      }
    },
    openItemsDialog(category) {
      this.selectedCategory = category;
      this.itemsDialog = true;
    },
    formatPrice(value) {
      if (!value) return "0";
      return new Intl.NumberFormat("id-ID", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [];
      for (let i = currentYear - 2; i <= currentYear; i++) {
        this.yearOptions.push(i);
      }
    },
  },
  mounted() {
    this.generateYearOptions();
  },
};
</script>

<style></style>
